import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import SectionCTA from "@/components/SectionCTA";
import api from "@/lib/api";
import { swatchUrl } from "@/components/studio/tileTexture";

const FAMILY = { Marmo: "Marble", Pietra: "Stone", Legno: "Wood", Classico: "The Classics" };

const Piece = ({ c, big }) => (
  <Link to={`/studio?tile=${c.id}`} className={`group relative block overflow-hidden rounded-2xl ${big ? "sm:col-span-2 sm:row-span-2" : ""}`} data-testid={`collection-item-${c.id}`}>
    {c.swatch ? <img src={swatchUrl(c.swatch)} alt={c.name} className="w-full h-full min-h-[240px] object-cover transition-transform duration-[1200ms] group-hover:scale-[1.06]" /> : <div className="w-full h-full min-h-[240px]" style={{ background: c.colour }} />}
    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/35 to-transparent" />
    <div className="absolute bottom-0 inset-x-0 p-5">
      <p className="text-[11px] font-mono uppercase tracking-widest text-rossi-gold">{c.material} · {c.finish} · {c.tile_w}×{c.tile_h}mm</p>
      <h3 className={`font-serif text-white mt-1 ${big ? "text-3xl sm:text-4xl" : "text-2xl"}`}>{c.name}</h3>
      {big && <p className="text-sm text-white/75 mt-2 max-w-md">{c.description}</p>}
      <div className="flex items-center justify-between mt-3">
        {c.price_m2 ? <span className="text-sm text-white/85 font-mono">from £{c.price_m2}/m²</span> : <span />}
        <span className="text-xs text-rossi-gold flex items-center gap-1 opacity-80 group-hover:opacity-100 transition-opacity">Try in The Studio <ArrowUpRight size={13} /></span>
      </div>
    </div>
  </Link>
);

export default function Collections() {
  const [items, setItems] = useState(null);
  const [fam, setFam] = useState("All");
  useEffect(() => { api.get("/collections").then((r) => setItems(r.data || [])).catch(() => setItems([])); }, []);
  const families = items ? [...new Set(items.map((c) => c.family))] : [];
  const shown = fam === "All" ? families : [fam];
  return (
    <div data-testid="collections-page">
      <Seo title="Tile Collections — The Rossi Lookbook" description="Explore the Rossi Ceramics tile collections: Italian-inspired marble, stone, wood and classic tiles, installed with British craftsmanship." />
      <section className="pt-36 lg:pt-44 pb-12 container-rossi">
        <Reveal>
          <p className="eyebrow mb-4">The Lookbook</p>
          <h1 className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl text-rossi-cream leading-tight max-w-3xl">Collezioni — <span className="gold-text">materials with a story.</span></h1>
          <p className="mt-5 text-rossi-cream/75 max-w-xl">Hand-picked surfaces in the Italian tradition. Tap any tile to lay it in your own room in The Studio.</p>
        </Reveal>
        <div className="flex flex-wrap gap-2 mt-10" data-testid="collection-filters">
          {["All", ...families].map((f) => <button key={f} onClick={() => setFam(f)} className={`px-4 py-2 rounded-full text-sm transition-colors ${fam === f ? "bg-rossi-gold text-rossi-bg" : "border border-rossi-gold/30 text-rossi-cream/80 hover:bg-rossi-gold/10"}`} data-testid={`collection-filter-${f}`}>{f === "All" ? "All" : `${f} · ${FAMILY[f] || f}`}</button>)}
        </div>
      </section>
      <section className="container-rossi pb-20 space-y-20">
        {!items && <Loader2 className="animate-spin text-rossi-gold" />}
        {items && items.length === 0 && <p className="text-rossi-stone">New collections are arriving soon.</p>}
        {shown.map((f) => (
          <div key={f} data-testid={`collection-family-${f}`}>
            <Reveal>
              <div className="flex items-baseline gap-4 mb-6 border-b border-white/10 pb-4">
                <h2 className="font-serif italic text-4xl sm:text-5xl text-rossi-cream">{f}</h2>
                <span className="text-sm font-mono uppercase tracking-widest text-rossi-stone">{FAMILY[f] || ""}</span>
              </div>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-3 auto-rows-[260px] gap-4">
              {items.filter((c) => c.family === f).map((c, i) => <Piece key={c.id} c={c} big={i === 0} />)}
            </div>
          </div>
        ))}
      </section>
      <SectionCTA />
    </div>
  );
}
